import { Link, useLocation } from 'react-router-dom';
import { XMarkIcon, CheckCircleIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import { useQuickStart } from '../context/QuickStartContext';
import { useAuth } from '../hooks/useAuth';
import { useSalesforce } from '../hooks/useSalesforce';
import { useEffect, useMemo } from 'react';

export default function QuickStartBanner() {
  const { isActive, currentStep, stepsCompleted, dismissQuickStart, isDismissed, markStepComplete } = useQuickStart();
  const { user } = useAuth();
  const { orgs } = useSalesforce();
  const location = useLocation();

  // Memoize renumbered steps to prevent infinite render loops
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS (Rules of Hooks)
  // Step information with custom path matchers
  // Note: Step 5 (View Flow Detail) is conditional and only shown if user has connected orgs
  const hasOrgs = orgs.length > 0;

  const renumberedSteps = useMemo(() => {
    const allSteps = [
      {
        number: 1,
        title: 'Connect a Salesforce Org',
        completed: stepsCompleted.orgConnected,
        action: '/connect-salesforce',
        stepKey: 'orgConnected' as const,
        matches: (pathname: string) => pathname === '/connect-salesforce'
      },
      {
        number: 2,
        title: 'View and Manage Connected Orgs',
        completed: stepsCompleted.viewedOrgs,
        action: '/orgs',
        stepKey: 'viewedOrgs' as const,
        matches: (pathname: string) => pathname === '/orgs' || pathname.startsWith('/orgs/')
      },
      {
        number: 3,
        title: 'Explore the Dashboard',
        completed: stepsCompleted.exploredDashboard,
        action: '/dashboard',
        stepKey: 'exploredDashboard' as const,
        matches: (pathname: string) => {
          // Match /dashboard or /:orgId/dashboard
          return pathname === '/dashboard' || /^\/[^\/]+\/dashboard$/.test(pathname)
        }
      },
      {
        number: 4,
        title: 'View and Filter Flows',
        completed: stepsCompleted.viewedFlows,
        action: '/flows',
        stepKey: 'viewedFlows' as const,
        matches: (pathname: string) => {
          // Match /flows or /:orgId/flows EXACTLY (not flow detail pages)
          return pathname === '/flows' || /^\/[^\/]+\/flows$/.test(pathname)
        }
      },
      {
        number: 5,
        title: 'View Flow Detail',
        completed: stepsCompleted.viewedFlowDetail,
        action: '/flows?autoSelectFirst=true',  // Auto-navigate to first flow
        stepKey: 'viewedFlowDetail' as const,
        tooltip: 'View details of a flow',
        matches: (pathname: string) => {
          // Match /flows/:orgId/:flowId or /:orgId/flows/:flowId or /:orgId/flow/:flowId (flow detail pages)
          return /^\/flows\/[^\/]+\/[^\/]+/.test(pathname) ||
                 /^\/[^\/]+\/flows\/[^\/]+/.test(pathname) ||
                 /^\/[^\/]+\/flow\/[^\/]+/.test(pathname)
        },
        conditional: true  // Only show if user has connected orgs
      },
      {
        number: 6,
        title: 'See Field Impact',
        completed: stepsCompleted.viewedFields,
        action: '/fields',
        stepKey: 'viewedFields' as const,
        matches: (pathname: string) => {
          // Match /fields or /:orgId/fields
          return pathname === '/fields' || /^\/[^\/]+\/fields$/.test(pathname)
        }
      },
      {
        number: 7,
        title: 'See Entry Criteria',
        completed: stepsCompleted.viewedEntryCriteria,
        action: '/entry-criteria',
        stepKey: 'viewedEntryCriteria' as const,
        matches: (pathname: string) => {
          // Match /entry-criteria or /:orgId/entry-criteria
          return pathname === '/entry-criteria' || /^\/[^\/]+\/entry-criteria$/.test(pathname)
        }
      }
    ];

    // Filter steps based on conditions (skip Step 5 if no orgs connected)
    const steps = hasOrgs ? allSteps : allSteps.filter(step => !step.conditional);
    return steps.map((step, index) => ({ ...step, number: index + 1 }));
  }, [hasOrgs, stepsCompleted.orgConnected, stepsCompleted.viewedOrgs, stepsCompleted.exploredDashboard,
      stepsCompleted.viewedFlows, stepsCompleted.viewedFlowDetail, stepsCompleted.viewedFields,
      stepsCompleted.viewedEntryCriteria]);

  // Check if any steps have been completed (tour has started)
  const tourStarted = renumberedSteps.some(s => s.completed);

  // Auto-mark steps as complete when visiting their pages
  useEffect(() => {
    // Check if we just reset Quick Start - if so, don't auto-check THIS page
    const justReset = sessionStorage.getItem('quickStartJustReset');
    if (justReset) {
      sessionStorage.removeItem('quickStartJustReset');
      return;
    }

    const currentPageStep = renumberedSteps.find(step => step.matches(location.pathname));

    if (currentPageStep && !currentPageStep.completed) {
      // Only auto-check if:
      // 1. This is Step 1 (always auto-check Step 1 to start the tour), OR
      // 2. Tour has already started (Step 1 is complete)
      if (currentPageStep.number === 1 || tourStarted) {
        const timer = setTimeout(() => {
          markStepComplete(currentPageStep.stepKey);
        }, 300);

        return () => clearTimeout(timer);
      }
    }
  }, [location.pathname, markStepComplete, tourStarted, renumberedSteps]);

  // Find current page and first incomplete
  const currentStepByUrl = renumberedSteps.find(step => step.matches(location.pathname));
  const firstIncomplete = renumberedSteps.find(step => !step.completed);
  const allStepsComplete = renumberedSteps.every(step => step.completed);

  // Show "start tour" message if tour hasn't started yet
  const showStartMessage = !tourStarted;

  // Determine which step to display:
  // - If on a Quick Start page, show that step
  // - Otherwise, show first incomplete step
  // - If all complete, show current page step or last step
  const displayStep = currentStepByUrl || firstIncomplete || (allStepsComplete ? renumberedSteps[renumberedSteps.length - 1] : renumberedSteps[0]);

  // Highlight the current step only if tour has started AND we're on a QS page
  const highlightStepNumber = showStartMessage ? null : currentStepByUrl?.number;

  // Extract orgId from current URL for building org-specific navigation
  const orgIdMatch = location.pathname.match(/^\/([^\/]+)\//);
  const currentOrgId = orgIdMatch ? orgIdMatch[1] : null;

  // Simple logic based on displayStep (no flash because we use the same source of truth):
  // On last step? Only show < (so user can navigate back)
  // On Step 1? Only show >
  // Otherwise? Show both

  const prevStep = (showStartMessage || displayStep.number === 1) ? null : renumberedSteps[displayStep.number - 2];
  const nextStep = (displayStep.number === renumberedSteps.length) ? null : (showStartMessage ? renumberedSteps[0] : renumberedSteps[displayStep.number]);

  // Build proper action URLs for Step 5 (View Flow Detail - needs orgId for auto-select to work)
  let prevStepWithUrl = prevStep;
  let nextStepWithUrl = nextStep;

  if (prevStepWithUrl && prevStepWithUrl.stepKey === 'viewedFlowDetail' && currentOrgId) {
    prevStepWithUrl = { ...prevStepWithUrl, action: `/${currentOrgId}/flows?autoSelectFirst=true` };
  }
  if (nextStepWithUrl && nextStepWithUrl.stepKey === 'viewedFlowDetail' && currentOrgId) {
    nextStepWithUrl = { ...nextStepWithUrl, action: `/${currentOrgId}/flows?autoSelectFirst=true` };
  }

  // Conditional returns AFTER all hooks are called (Rules of Hooks)
  // Debug logging
  console.log('QuickStartBanner render:', { isActive, isDismissed, user: !!user, hasOrgs });

  // Don't show if not authenticated
  if (!user) {
    console.log('QuickStartBanner: No user, hiding');
    return null;
  }

  // Don't show if dismissed or not active
  if (!isActive || isDismissed) {
    console.log('QuickStartBanner: Not active or dismissed, hiding', { isActive, isDismissed });
    return null;
  }

  return (
    <div className="bg-blue-700 text-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          {/* Left: Icon + Progress Text */}
          <div className="flex items-center space-x-3">
            <RocketLaunchIcon className="h-6 w-6 flex-shrink-0" />
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2">
              {showStartMessage ? (
                <span className="font-semibold text-sm sm:text-base">
                  Click &gt; to Start Guided Tour
                </span>
              ) : (
                <>
                  <span className="font-semibold text-sm sm:text-base">
                    Quick Start: Step {displayStep.number} of {renumberedSteps.length}
                  </span>
                  <span className="hidden sm:inline text-blue-100">—</span>
                  <span className="text-blue-100 text-sm">
                    {displayStep.title}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Right: Navigation Arrows + Progress Indicators (fixed position) */}
          <div className="hidden md:flex items-center gap-3">
            {/* Container with fixed width to prevent jumping */}
            <div className="flex items-center gap-3">
              {/* Previous Button (Left of circles) */}
              <div className="w-8 h-8 flex items-center justify-center">
                {prevStepWithUrl && (
                  <Link
                    to={prevStepWithUrl.action}
                    className="inline-flex items-center justify-center h-8 w-8 bg-white text-blue-700 rounded-full hover:bg-blue-50 transition-colors shadow-sm"
                    title={`Previous: ${prevStepWithUrl.title}`}
                  >
                    <span className="text-lg font-bold">&lt;</span>
                  </Link>
                )}
              </div>

              {/* Progress Indicators */}
              <div className="flex items-center gap-2">
                {renumberedSteps.map((step) => {
                  // Build proper URL for View Flow Detail step with orgId
                  const stepAction = step.stepKey === 'viewedFlowDetail' && currentOrgId
                    ? `/${currentOrgId}/flows?autoSelectFirst=true`
                    : step.action;

                  return (
                    <Link
                      key={step.number}
                      to={stepAction}
                      className={`flex items-center justify-center h-8 w-8 rounded-full border-2 transition-colors cursor-pointer hover:scale-110 ${
                        step.completed
                          ? 'bg-green-500 border-green-400 hover:bg-green-600'
                          : step.number === highlightStepNumber
                          ? 'bg-blue-500 border-blue-300 hover:bg-blue-600'
                          : 'bg-blue-700 border-blue-500 hover:bg-blue-600'
                      }`}
                      title={step.tooltip || step.title}
                    >
                      {step.completed ? (
                        <CheckCircleIcon className="h-5 w-5 text-white" />
                      ) : (
                        <span className="text-sm font-medium text-white">{step.number}</span>
                      )}
                    </Link>
                  );
                })}
              </div>

              {/* Next Button (Right of circles) */}
              <div className="w-8 h-8 flex items-center justify-center">
                {nextStepWithUrl && (
                  <Link
                    to={nextStepWithUrl.action}
                    className="inline-flex items-center justify-center h-8 w-8 bg-white text-blue-700 rounded-full hover:bg-blue-50 transition-colors shadow-sm"
                    title={`Next: ${nextStepWithUrl.title}`}
                  >
                    <span className="text-lg font-bold">&gt;</span>
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Right: Quick Start Guide Link + Dismiss Button */}
          <div className="flex items-center gap-2">
            <Link
              to="/help/quick-start"
              className="hidden lg:inline-flex items-center px-3 py-1.5 text-sm text-blue-100 hover:text-white hover:bg-blue-800 rounded-md transition-colors"
              title="View Quick Start Guide"
            >
              View Quick Start Guide
            </Link>
            <button
              onClick={dismissQuickStart}
              className="inline-flex items-center p-1.5 text-blue-100 hover:text-white hover:bg-blue-800 rounded-md transition-colors"
              aria-label="Dismiss Quick Start"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
